import { useState, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Chip,
  Grid, IconButton, InputAdornment, LinearProgress, MenuItem, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField as MuiTextField, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TimelineIcon from '@mui/icons-material/Timeline';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import GavelIcon from '@mui/icons-material/Gavel';
import PieChartIcon from '@mui/icons-material/PieChart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import BusinessIcon from '@mui/icons-material/Business';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useGetAePipelineQuery } from '../../services/aePipelineApi';
import brokerCompanyApi from '../../services/brokerCompanyApi';
import { authService } from '../../services/authService';
import StatusBadge, { PIPELINE_STATUS_MAP, BROKER_STATUS_MAP } from '../../components/shared/StatusBadge';
import SectionHeader from '../../components/shared/SectionHeader';
import PageHeader from '../../components/shared/PageHeader';
import StatCard from '../../components/shared/StatCard';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import EmptyState from '../../components/shared/EmptyState';
import TrendChart from '../../components/shared/TrendChart';
import { Roles } from '../../constants';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';

const fmtDollar = (v) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtShortDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const EXTENDED_STAGE_MAP = {
  ...PIPELINE_STATUS_MAP,
  Origination: { variant: 'info', label: 'Origination' },
  Processing: { variant: 'cyan', label: 'Processing' },
  Underwriting: { variant: 'info', label: 'Underwriting' },
  Closed: { variant: 'success', label: 'Closed' },
  Funded: { variant: 'cyan', label: 'Funded' },
};

const AeDashboardPage = () => {
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const isAeRole = currentUser?.role === Roles.AccountExecutive;
  const { data, isLoading, error, refetch } = useGetAePipelineQuery();
  const { data: assignedCompanies = [] } = brokerCompanyApi.endpoints.getMyAssignedBrokerCompanies.useQuery(undefined, { skip: !isAeRole });
  const [selectedBroker, setSelectedBroker] = useState('all');
  const [pipelineSearch, setPipelineSearch] = useState('');
  const [pipelineSort, setPipelineSort] = useState({ field: 'lastUpdated', dir: 'desc' });

  const filtered = useMemo(() => {
    if (!data) return null;
    if (selectedBroker === 'all') return data;

    const loans = data.loans.filter(
      (l) => (l.brokerCompanyNmls || l.brokerCompanyName) === selectedBroker
    );
    const s = data.summary;
    const totalAmt = loans.reduce((acc, l) => acc + (l.loanAmount || 0), 0);
    const rateLoans = loans.filter((l) => l.noteRate && l.loanAmount > 0);
    const wac = rateLoans.length > 0
      ? rateLoans.reduce((acc, l) => acc + l.noteRate * l.loanAmount, 0) / totalAmt
      : 0;
    const ficoLoans = loans.filter((l) => l.creditScore > 0);

    return {
      ...data,
      loans,
      summary: {
        ...s,
        totalActiveLoans: loans.length,
        pipelineVolume: totalAmt,
        weightedAvgCoupon: Math.round(wac * 1000) / 1000,
        avgLoanAmount: loans.length > 0 ? Math.round(totalAmt / loans.length) : 0,
        avgFico: ficoLoans.length > 0
          ? Math.round(ficoLoans.reduce((acc, l) => acc + l.creditScore, 0) / ficoLoans.length)
          : 0,
      },
    };
  }, [data, selectedBroker]);

  const pipelineLoans = useMemo(() => {
    if (!filtered?.loans) return [];
    let loans = [...filtered.loans];

    if (pipelineSearch) {
      const q = pipelineSearch.toLowerCase();
      loans = loans.filter(
        (l) =>
          (l.borrowerNames || '').toLowerCase().includes(q) ||
          (l.primaryBorrowerFullName || '').toLowerCase().includes(q) ||
          (l.loanNumber?.toString() || '').includes(q) ||
          (l.brokerCompanyName || '').toLowerCase().includes(q) ||
          (l.loanOriginator || '').toLowerCase().includes(q)
      );
    }

    const { field, dir } = pipelineSort;
    loans.sort((a, b) => {
      let av = a[field], bv = b[field];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });

    return loans;
  }, [filtered, pipelineSearch, pipelineSort]);

  const brokerOptions = useMemo(() => {
    if (!data?.brokerLeaderboard) return [];
    return data.brokerLeaderboard.map((b) => ({
      key: b.brokerCompanyNmls || b.brokerCompanyName,
      label: b.brokerCompanyName + (b.brokerCompanyNmls ? ` (${b.brokerCompanyNmls})` : ''),
    }));
  }, [data]);

  const handleSort = (field) => {
    setPipelineSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: 'asc' }
    );
  };

  const SortHeader = ({ field, children, align }) => (
    <TableCell align={align} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => handleSort(field)}>
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent={align === 'right' ? 'flex-end' : 'flex-start'}>
        <span>{children}</span>
        {pipelineSort.field === field
          ? (pipelineSort.dir === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)
          : <SwapVertIcon sx={{ fontSize: 14, opacity: 0.25 }} />}
      </Stack>
    </TableCell>
  );

  if (isLoading) {
    return <LoadingSkeleton variant="page" cards={4} />;
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" action={<Button size="small" color="error" variant="outlined" onClick={refetch}>Retry</Button>}>
          Failed to load dashboard data. {getAxiosErrorMessage(error, '')}
        </Alert>
      </Box>
    );
  }

  const s = filtered?.summary;
  const sb = filtered?.statusBreakdown || data?.statusBreakdown;
  const pm = filtered?.productMix || data?.productMix;
  const trend = data?.activityTrend?.days || [];
  const leaderboard = data?.brokerLeaderboard || [];

  const today = new Date();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const daysLeft = daysInMonth - today.getDate();

  const pmTotal = pm ? (pm.conventional + pm.fha + pm.va + pm.usda + pm.jumbo + pm.otherType) || 1 : 1;
  const purposeTotal = pm ? (pm.purchase + pm.refinance + pm.otherPurpose) || 1 : 1;

  const MtdCard = ({ title, value, todayVal, color }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <SectionHeader title={title} />
        <Typography variant="h3" fontWeight={700} color={color} className="tabular-nums" sx={{ mb: 1 }}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">
          Today: <Typography component="span" fontWeight={700} color={color}>{todayVal}</Typography>
        </Typography>
      </CardContent>
    </Card>
  );

  const ProgressRow = ({ label, value, pct, color }) => (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={700}>{Math.round(pct)}% ({value})</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: color } }} />
    </Box>
  );

  const UwRow = ({ label, value, color }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
        <Typography variant="body2">{label}</Typography>
      </Stack>
      <Typography variant="body2" fontWeight={700}>{value || 0}</Typography>
    </Stack>
  );

  return (
    <Box>
      <PageHeader
        title="AE Dashboard"
        subtitle="Real-time pipeline metrics and broker performance"
        actions={(
          <>
            <MuiTextField
              select
              size="small"
              value={selectedBroker}
              onChange={(e) => setSelectedBroker(e.target.value)}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="all">All Brokers</MenuItem>
              {brokerOptions.map((b) => <MenuItem key={b.key} value={b.key}>{b.label}</MenuItem>)}
            </MuiTextField>
            <IconButton onClick={refetch} title="Refresh"><RefreshIcon /></IconButton>
          </>
        )}
      />

      {/* Assigned Broker Companies (AE only) */}
      {isAeRole && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={<SectionHeader title={`My Broker Companies (${assignedCompanies.length})`} icon={<BusinessIcon fontSize="small" />} sx={{ mb: 0 }} />}
            subheader="Companies assigned to you"
            subheaderTypographyProps={{ variant: 'caption' }}
          />
          {assignedCompanies.length === 0 ? (
            <EmptyState compact icon={<BusinessIcon />} title="No broker companies assigned" description="Companies assigned to you will appear here." />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Company Name</TableCell>
                    <TableCell>NMLS</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignedCompanies.map((company) => (
                    <TableRow key={company.id} hover>
                      <TableCell><Typography variant="body2" fontWeight={700}>{company.name}</Typography></TableCell>
                      <TableCell><StatusBadge variant="info">{company.nmls}</StatusBadge></TableCell>
                      <TableCell><StatusBadge status={company.status} map={BROKER_STATUS_MAP} /></TableCell>
                      <TableCell>{fmtDate(company.dateCreated)}</TableCell>
                      <TableCell>
                        <IconButton size="small" color="primary" component={RouterLink} to={`/lender/brokers/${company.id}`}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Card>
      )}

      {/* Calendar */}
      <Alert severity="info" icon={<CalendarMonthIcon />} sx={{ mb: 3 }}>
        <strong>Month Progress:</strong> Day {today.getDate()} of {daysInMonth} •{' '}
        <strong>{daysLeft} days remaining</strong> •{' '}
        {Math.round((today.getDate() / daysInMonth) * 100)}% complete
      </Alert>

      {/* Lock Alerts */}
      {(s?.locksExpiringIn3Days > 0 || s?.locksExpired > 0) && (
        <Alert severity={s?.locksExpired > 0 ? 'error' : 'warning'} icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
          {s?.locksExpired > 0 && <strong>{s.locksExpired} expired lock{s.locksExpired !== 1 ? 's' : ''}</strong>}
          {s?.locksExpired > 0 && s?.locksExpiringIn3Days > 0 && ' • '}
          {s?.locksExpiringIn3Days > 0 && <strong>{s.locksExpiringIn3Days} lock{s.locksExpiringIn3Days !== 1 ? 's' : ''} expiring in 3 days</strong>}
          {s?.locksExpiringIn7Days > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({s.locksExpiringIn7Days} more within 7 days)
            </Typography>
          )}
        </Alert>
      )}

      {/* Portfolio Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, lg: 3 }}><StatCard label="Weighted Avg Coupon (WAC)" value={`${(s?.weightedAvgCoupon || 0).toFixed(3)}%`} caption="Portfolio average rate" /></Grid>
        <Grid size={{ xs: 6, lg: 3 }}><StatCard label="Weighted Avg DTI" value={`${(s?.weightedAvgDti || 0).toFixed(1)}%`} caption="Portfolio average DTI" /></Grid>
        <Grid size={{ xs: 6, lg: 3 }}><StatCard label="Avg Loan Amount" value={fmtDollar(s?.avgLoanAmount)} caption="Average loan size" /></Grid>
        <Grid size={{ xs: 6, lg: 3 }}><StatCard label="Avg FICO Score" value={s?.avgFico || '—'} caption="Portfolio average" /></Grid>
      </Grid>

      {/* MTD Performance */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 4 }}><MtdCard title="Submissions MTD" value={s?.submissionsMtd || 0} todayVal={s?.submissionsToday || 0} color="primary.main" /></Grid>
        <Grid size={{ xs: 12, lg: 4 }}><MtdCard title="Locks MTD" value={s?.locksMtd || 0} todayVal={s?.locksToday || 0} color="success.main" /></Grid>
        <Grid size={{ xs: 12, lg: 4 }}><MtdCard title="Fundings MTD" value={s?.fundingsMtd || 0} todayVal={s?.fundingsToday || 0} color="info.main" /></Grid>
      </Grid>

      {/* 7-Day Trend + Pipeline Health */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title={<SectionHeader title="7-Day Activity Trend" icon={<TimelineIcon fontSize="small" />} sx={{ mb: 0 }} />} />
            {trend.length > 0 ? (
              <CardContent sx={{ pt: 0 }}>
                <TrendChart
                  data={trend.map((d) => ({
                    label: fmtShortDate(d.date),
                    submissions: d.submissions,
                    locks: d.locks,
                    fundings: d.fundings,
                  }))}
                  series={[
                    { key: 'submissions', label: 'Submissions', color: 'primary' },
                    { key: 'locks', label: 'Locks', color: 'success' },
                    { key: 'fundings', label: 'Fundings', color: 'info' },
                  ]}
                  height={260}
                />
              </CardContent>
            ) : (
              <EmptyState compact icon={<TimelineIcon />} title="No activity data" description="Activity over the last 7 days will appear here." />
            )}
          </Card>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title={<SectionHeader title="Pipeline Health" icon={<MonitorHeartIcon fontSize="small" />} sx={{ mb: 0 }} />} />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2">Pull-Through Ratio</Typography>
                  <Typography variant="body2" fontWeight={700} color="success.main">{(s?.pullThroughRatio || 0).toFixed(1)}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={Math.min(s?.pullThroughRatio || 0, 100)} color="success" sx={{ height: 6, borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">{s?.fundingsMtd || 0} funded / {s?.locksMtd || 0} locked</Typography>
              </Box>
              {[
                { label: 'Pipeline Volume', value: fmtDollar(s?.pipelineVolume) },
                { label: 'Active Brokers', value: s?.activeBrokers || 0 },
                { label: 'Active Loans', value: s?.totalActiveLoans || 0 },
                { label: 'Avg Days to Close', value: `${s?.avgDaysToClose || 0} days` },
              ].map(({ label, value }) => (
                <Stack key={label} direction="row" justifyContent="space-between" sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="body2">{label}</Typography>
                  <Typography variant="body2" fontWeight={700}>{value}</Typography>
                </Stack>
              ))}
              {s?.staleLoans > 0 && (
                <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
                  <Typography variant="body2">Stale Loans</Typography>
                  <Typography variant="body2" fontWeight={700} color="warning.main">{s.staleLoans}</Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status + UW Decisions + Product Mix */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title={<SectionHeader title="Pipeline Status" icon={<ListAltIcon fontSize="small" />} sx={{ mb: 0 }} />} />
            <CardContent>
              {[
                { key: 'submitted', status: 'Submitted', desc: 'New submissions' },
                { key: 'inReview', status: 'In Review', desc: 'Active underwriting' },
                { key: 'approved', status: 'Approved', desc: 'Approved, pending lock' },
                { key: 'locked', status: 'Locked', desc: 'Rate locked' },
                { key: 'funded', status: 'Funded', desc: 'Closed / funded' },
              ].map(({ key, status, desc }) => (
                <Stack key={key} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <StatusBadge status={status} map={PIPELINE_STATUS_MAP} />
                    <Typography variant="body2">{desc}</Typography>
                  </Stack>
                  <Typography variant="h6" fontWeight={700}>{sb?.[key] || 0}</Typography>
                </Stack>
              ))}
              {sb?.other > 0 && (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label="Other" size="small" variant="outlined" />
                    <Typography variant="body2">Other stages</Typography>
                  </Stack>
                  <Typography variant="h6" fontWeight={700}>{sb.other}</Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title={<SectionHeader title="UW Decision Breakdown" icon={<GavelIcon fontSize="small" />} sx={{ mb: 0 }} />} />
            <CardContent>
              <UwRow label="Approved w/ Conditions" value={s?.uwApprovedWithConditions} color="warning.main" />
              <UwRow label="Clear to Close" value={s?.uwClearToClose} color="success.main" />
              <UwRow label="Suspended" value={s?.uwSuspended} color="warning.dark" />
              <UwRow label="Denied" value={s?.uwDenied} color="error.main" />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title={<SectionHeader title="Product Mix & Purpose" icon={<PieChartIcon fontSize="small" />} sx={{ mb: 0 }} />} />
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Product Type</Typography>
              {[
                { label: 'Conventional', val: pm?.conventional, color: 'primary.main' },
                { label: 'FHA', val: pm?.fha, color: 'success.main' },
                { label: 'VA', val: pm?.va, color: 'info.main' },
                { label: 'USDA', val: pm?.usda, color: 'secondary.main' },
                { label: 'Jumbo', val: pm?.jumbo, color: 'warning.main' },
              ].filter(({ val }) => val > 0).map(({ label, val, color }) => (
                <ProgressRow key={label} label={label} value={val} pct={(val / pmTotal) * 100} color={color} />
              ))}

              <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1.5 }}>Loan Purpose</Typography>
              {[
                { label: 'Purchase', val: pm?.purchase, color: 'info.main' },
                { label: 'Refinance', val: pm?.refinance, color: 'secondary.main' },
              ].filter(({ val }) => val > 0).map(({ label, val, color }) => (
                <ProgressRow key={label} label={label} value={val} pct={(val / purposeTotal) * 100} color={color} />
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Broker Leaderboard */}
      {leaderboard.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={<SectionHeader title="Broker Leaderboard" icon={<EmojiEventsIcon fontSize="small" />} sx={{ mb: 0 }} />}
            subheader="Ranked by total pipeline volume"
            subheaderTypographyProps={{ variant: 'caption' }}
          />
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}>Rank</TableCell>
                  <TableCell>Broker Company</TableCell>
                  <TableCell>NMLS</TableCell>
                  <TableCell align="right">Volume</TableCell>
                  <TableCell align="right">Loans</TableCell>
                  <TableCell align="right">Avg Size</TableCell>
                  <TableCell align="right">Locks MTD</TableCell>
                  <TableCell align="right">Fundings MTD</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaderboard.map((b, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Chip label={`#${idx + 1}`} size="small" color={idx === 0 ? 'warning' : idx === 1 ? 'default' : idx === 2 ? 'secondary' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell><Typography variant="body2" fontWeight={700}>{b.brokerCompanyName}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{b.brokerCompanyNmls || '—'}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight={700} color="primary.main">{fmtDollar(b.volume)}</Typography></TableCell>
                    <TableCell align="right">{b.loanCount}</TableCell>
                    <TableCell align="right">{fmtDollar(b.avgLoanSize)}</TableCell>
                    <TableCell align="right">{b.locksMtd}</TableCell>
                    <TableCell align="right">{b.fundingsMtd}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Card>
      )}

      {/* Pipeline Table */}
      <Card>
        <CardHeader
          title={<SectionHeader title={`Loan Pipeline (${pipelineLoans.length})`} icon={<TableChartIcon fontSize="small" />} sx={{ mb: 0 }} />}
          action={
            <MuiTextField
              size="small"
              placeholder="Search borrower, loan #, broker..."
              value={pipelineSearch}
              onChange={(e) => setPipelineSearch(e.target.value)}
              sx={{ minWidth: 260 }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
            />
          }
        />
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& tbody tr': { cursor: 'pointer' }, '& tbody tr:hover': { bgcolor: 'action.hover' } }}>
            <TableHead>
              <TableRow>
                <SortHeader field="loanNumber">Loan #</SortHeader>
                <SortHeader field="borrowerNames">Borrower</SortHeader>
                <SortHeader field="brokerCompanyName">Broker</SortHeader>
                <SortHeader field="loanAmount" align="right">Amount</SortHeader>
                <SortHeader field="stage">Stage</SortHeader>
                <SortHeader field="noteRate" align="right">Rate</SortHeader>
                <SortHeader field="mortgageType">Type</SortHeader>
                <SortHeader field="lockExpirationDate">Lock Exp</SortHeader>
                <SortHeader field="lastUpdated">Updated</SortHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {pipelineLoans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {pipelineSearch ? 'No loans match your search' : 'No loans in pipeline'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pipelineLoans.map((loan) => {
                  const lockExpiring = loan.lockExpirationDate && new Date(loan.lockExpirationDate) < new Date(Date.now() + 3 * 86400000);
                  const lockExpired = loan.lockExpirationDate && new Date(loan.lockExpirationDate) < new Date();
                  return (
                    <TableRow key={loan.loanId} onClick={() => navigate(`/ae/loans/${loan.loanId}`)}>
                      <TableCell><Typography variant="body2" fontWeight={700} color="primary.main">{loan.loanNumber || '—'}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2">{loan.borrowerNames || loan.primaryBorrowerFullName || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{[loan.propertyCity, loan.propertyState].filter(Boolean).join(', ')}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{loan.brokerCompanyName || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{loan.loanOriginator || ''}</Typography>
                      </TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight={700}>{fmtDollar(loan.loanAmount)}</Typography></TableCell>
                      <TableCell><StatusBadge status={loan.stage} map={EXTENDED_STAGE_MAP} /></TableCell>
                      <TableCell align="right">{loan.noteRate ? `${loan.noteRate.toFixed(3)}%` : '—'}</TableCell>
                      <TableCell>{loan.mortgageType || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={lockExpired || lockExpiring ? 700 : 400} color={lockExpired ? 'error.main' : lockExpiring ? 'warning.main' : 'text.primary'}>
                          {fmtDate(loan.lockExpirationDate)}
                          {lockExpired && <WarningAmberIcon sx={{ fontSize: 14, ml: 0.5 }} />}
                        </Typography>
                      </TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{fmtDate(loan.lastUpdated)}</Typography></TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      </Card>
    </Box>
  );
};

export default AeDashboardPage;
